import { useNavigate } from "@tanstack/react-router";
import type { AssignableRole, Feature, TikTokRegion } from "@unbox/api";
import { merchantScopeRoles } from "@unbox/api";
import { AlertTriangle, ArrowLeft } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useCreateMerchant } from "@/api/admin/create-merchant";
import { useUploadMerchantLogo } from "@/api/admin/merchant-logo";
import { useAvailableTikTokShops, useTikTokShopBrands } from "@/api/admin/tiktok-shops";
import { useAddMerchantsToWorkspace, useAddUsersToWorkspace } from "@/api/admin/workspace-mutations";
import { useAdminWorkspaces } from "@/api/admin/workspaces";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { LogoPicker } from "@/components/application/logo-uploader/logo-picker";
import { IconTopNumber } from "@/components/base/application/progress-steps/step-base";
import { Button } from "@/components/base/buttons/button";
import { CheckboxGroup } from "@/components/base/checkbox-groups/checkbox-groups";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { TextArea } from "@/components/base/textarea/textarea";
import { Toggle } from "@/components/base/toggle/toggle";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { FormField, FormItem } from "@/components/ui/form";
import { WorkspaceUsersPicker } from "@/pages/admin/components/workspace-users-picker";
import { WorkspacesPicker } from "@/pages/admin/components/workspaces-picker";
import { success } from "@/providers/ToastProvider";
import { adminCreateMerchantRoute } from "@/router";
import { FEATURE_LABELS } from "@/utils/features";

const FEATURE_DESCRIPTIONS: Record<string, string> = {
  assurance: "Enables safety and content moderation features including risk review and flagging",
  insights: "Enables analytics, performance dashboards, smart insights, and reports",
  hideExplorers: "Hides creator, product, and video browsing sections from the merchant",
  noAffiliateAuthorisation: "Prevents the merchant from inviting or messaging creators",
};

export const AVAILABLE_FEATURES: Feature[] = [
  "assurance",
  "insights",
  "hideExplorers",
  "noAffiliateAuthorisation",
] as const;

const VERTICALS = [
  { id: "home_supplies", label: "Home Supplies" },
  { id: "kitchenware", label: "Kitchenware" },
  { id: "textiles_soft_furnishings", label: "Textiles & Soft Furnishings" },
  { id: "household_appliances", label: "Household Appliances" },
  { id: "womenswear_underwear", label: "Womenswear & Underwear" },
  { id: "modest_fashion", label: "Modest Fashion" },
  { id: "shoes", label: "Shoes" },
  { id: "beauty_personal_care", label: "Beauty & Personal Care" },
  { id: "phones_electronics", label: "Phones & Electronics" },
  { id: "computers_office_equipment", label: "Computers & Office Equipment" },
  { id: "pet_supplies", label: "Pet Supplies" },
  { id: "baby_maternity", label: "Baby & Maternity" },
  { id: "sports_outdoor", label: "Sports & Outdoor" },
  { id: "toys_hobbies", label: "Toys & Hobbies" },
  { id: "furniture", label: "Furniture" },
  { id: "tools_hardware", label: "Tools & Hardware" },
  { id: "home_improvement", label: "Home Improvement" },
  { id: "automotive_motorcycle", label: "Automotive & Motorcycle" },
  { id: "fashion_accessories", label: "Fashion Accessories" },
  { id: "food_beverages", label: "Food & Beverages" },
  { id: "health", label: "Health" },
  { id: "books_magazines_audio", label: "Books, Magazines & Audio" },
  { id: "kids_fashion", label: "Kids' Fashion" },
  { id: "menswear_underwear", label: "Menswear & Underwear" },
  { id: "luggage_bags", label: "Luggage & Bags" },
  { id: "virtual_products", label: "Virtual Products" },
  { id: "pre_owned", label: "Pre-Owned" },
  { id: "collectibles", label: "Collectibles" },
  { id: "jewelry_accessories_derivatives", label: "Jewelry Accessories & Derivatives" },
  { id: "bookings_vouchers", label: "Bookings & Vouchers" },
];

const BASE_STEPS = [
  { id: "tiktok-shop", label: "TikTok Shop" },
  { id: "brand-details", label: "Merchant details" },
  { id: "configuration", label: "Configuration" },
];

const WORKSPACE_STEPS = [
  { id: "workspace", label: "Workspace" },
  { id: "users", label: "Users" },
];

const toSlug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z]+/g, "_")
    .replace(/^_|_$/g, "");

type FormValues = {
  id: string;
  selectedShop: string;
  selectedBrand: string;
  brandLongName: string;
  brandShortName: string;
  brandHandle: string;
  description: string;
  vertical: string;
};

export const CreateMerchantPage = () => {
  const navigate = useNavigate();
  const STEPS = [...BASE_STEPS, ...WORKSPACE_STEPS];

  const { openId, shopId } = adminCreateMerchantRoute.useSearch();
  const form = useForm<FormValues>({
    mode: "onTouched",
    defaultValues: {
      id: "",
      selectedShop: "",
      selectedBrand: "",
      brandLongName: "",
      brandShortName: "",
      brandHandle: "",
      description: "",
      vertical: "",
    },
  });
  const { mutateAsync } = useCreateMerchant();
  const { mutateAsync: uploadLogo } = useUploadMerchantLogo();
  const { mutateAsync: addMerchantsToWorkspace } = useAddMerchantsToWorkspace();
  const { mutateAsync: addUsersToWorkspace } = useAddUsersToWorkspace();
  const { data: shopCredentials, isLoading: shopsLoading } = useAvailableTikTokShops();

  const [currentStep, setCurrentStep] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const [selectedFeatures, setSelectedFeatures] = useState<Feature[]>(["insights"]);
  const [showBrandSelect, setShowBrandSelect] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [ownerWorkspaceId, setOwnerWorkspaceId] = useState<string | null>(null);
  const [ownerError, setOwnerError] = useState<string | null>(null);
  const [userRoles, setUserRoles] = useState<Record<string, AssignableRole>>({});

  const { data: workspacesData } = useAdminWorkspaces(
    { offset: 0, limit: 500 },
    { column: "name", direction: "asc" },
    {},
  );
  const workspaceItems = (workspacesData?.workspaces ?? []).map((ws) => ({ id: ws.id, label: ws.name }));

  const availableShops = useMemo(() => {
    if (!shopCredentials) return [];
    return shopCredentials.flatMap((cred) =>
      cred.shops.map((shop) => ({
        key: `${cred.s3Key}|${shop.id}`,
        s3Key: cred.s3Key,
        sellerName: cred.sellerName,
        shopId: shop.id,
        shopName: shop.name,
        region: shop.region as TikTokRegion,
        cipher: shop.cipher,
        scopes: cred.scopes,
        openId: cred.openId,
      })),
    );
  }, [shopCredentials]);

  useEffect(() => {
    if ((shopId || openId) && availableShops.length > 0 && !form.getValues("selectedShop")) {
      const match = availableShops.find((s) => (shopId ? s.shopId === shopId : s.openId === openId));
      if (match) {
        form.setValue("selectedShop", match.key);
      }
    }
  }, [openId, shopId, availableShops, form]);

  const selectedShopKey = form.watch("selectedShop");
  const selectedShop = availableShops.find((s) => s.key === selectedShopKey);

  const brandsRegion = selectedShop?.region as TikTokRegion;
  const { data: brandsData, isLoading: brandsLoading } = useTikTokShopBrands(
    selectedShop?.s3Key ?? "",
    selectedShop?.cipher ?? "",
    brandsRegion,
  );

  const selectedBrandKey = form.watch("selectedBrand");
  const selectedBrand = brandsData?.brands.find((b) => b.id === selectedBrandKey);

  const AFFILIATE_SCOPES = ["seller.affiliate_messages.write", "seller.affiliate_collaboration.write"];
  const missingAffiliateScopes = selectedShop ? AFFILIATE_SCOPES.filter((s) => !selectedShop.scopes.includes(s)) : [];

  // Reset form when shop changes
  useEffect(() => {
    form.reset({
      selectedShop: selectedShopKey,
      selectedBrand: "",
      id: selectedShop ? toSlug(selectedShop.shopName) : "",
      brandLongName: selectedShop?.shopName ?? "",
      brandShortName: selectedShop?.shopName ?? "",
      brandHandle: "",
      description: "",
      vertical: "",
    });
    setShowBrandSelect(false);
    setLogoFile(null);
    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoPreviewUrl(null);
    setLogoError(null);
    setSelectedFeatures(
      selectedShop && missingAffiliateScopes.length > 0 ? ["insights", "noAffiliateAuthorisation"] : ["insights"],
    );
  }, [selectedShopKey]);

  // Update merchant ID and brand names when brand changes
  useEffect(() => {
    if (!selectedShop) return;
    const base = toSlug(selectedShop.shopName);
    if (selectedBrand) {
      form.setValue("id", `${base}_${toSlug(selectedBrand.name)}`);
      form.setValue("brandLongName", selectedBrand.name);
      form.setValue("brandShortName", selectedBrand.name);
    } else {
      form.setValue("id", base);
      form.setValue("brandLongName", selectedShop.shopName);
      form.setValue("brandShortName", selectedShop.shopName);
    }
  }, [selectedBrandKey, selectedBrand, selectedShop, form]);

  const getStepFields = (step: number): (keyof FormValues)[] => {
    switch (step) {
      case 0:
        return showBrandSelect ? ["selectedShop", "selectedBrand"] : ["selectedShop"];
      case 1:
        return ["id", "brandLongName", "brandHandle", "description", "vertical"];
      case 2:
        return ["brandShortName"];
      default:
        return [];
    }
  };

  // Validates step state that lives outside react-hook-form (e.g. the logo file).
  // Returns false and surfaces the inline error if the step isn't ready to advance.
  const validateStepExtras = (step: number): boolean => {
    if (step === 2 && !logoFile) {
      setLogoError("Logo is required");
      return false;
    }
    if (step === 3 && !ownerWorkspaceId) {
      setOwnerError("Owner workspace is required");
      return false;
    }
    return true;
  };

  const goToStep = (step: number) => {
    setCurrentStep(step);
    contentRef.current?.scrollTo({ top: 0 });
  };

  const isStepValid = async (step: number): Promise<boolean> => {
    const hookFormValid = await form.trigger(getStepFields(step));
    const extrasValid = validateStepExtras(step);
    return hookFormValid && extrasValid;
  };

  const handleNext = async () => {
    if (await isStepValid(currentStep)) {
      goToStep(Math.min(currentStep + 1, STEPS.length - 1));
    }
  };

  const handleStepClick = async (targetStep: number) => {
    if (targetStep <= currentStep) {
      goToStep(targetStep);
      return;
    }
    for (let i = currentStep; i < targetStep; i++) {
      if (!(await isStepValid(i))) {
        goToStep(i);
        return;
      }
    }
    goToStep(targetStep);
  };

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    if (!selectedShop) return;
    if (!logoFile) {
      setLogoError("Logo is required");
      return;
    }

    await mutateAsync({
      id: values.id,
      name: values.brandShortName,
      ttsShopId: selectedShop.shopId,
      ttsTokenS3Key: selectedShop.s3Key,
      ttsRegion: brandsRegion,
      ownerWorkspaceId: ownerWorkspaceId ?? undefined,
      ttsBrandId: selectedBrand?.id,
      brandLongName: values.brandLongName,
      brandShortName: values.brandShortName,
      description: values.description,
      vertical: values.vertical,
      sellerHandle: values.brandHandle?.replace(/^@/, ""),
      sellerName: selectedShop.sellerName,
      features: selectedFeatures,
    });

    await uploadLogo({ merchantId: values.id, file: logoFile });

    // Bucket the selected users by chosen role and grant each bucket access to
    // the new merchant with that role (UNB-2001). Create-merchant only offers
    // merchant_admin / user, so no workspace-admin grant here.
    const roleBuckets: Record<AssignableRole, string[]> = { user: [], merchant_admin: [], workspace_admin: [] };
    for (const [userId, role] of Object.entries(userRoles)) roleBuckets[role].push(userId);

    for (const workspaceId of selectedWorkspaceIds) {
      await addMerchantsToWorkspace({ workspaceId, merchantIds: [values.id] });
      for (const role of ["user", "merchant_admin"] as const) {
        if (roleBuckets[role].length > 0) {
          await addUsersToWorkspace({
            workspaceId,
            userIds: roleBuckets[role],
            merchants: { [values.id]: role },
          });
        }
      }
    }

    success("Merchant created");
    navigate({ to: `/admin/merchants/${values.id}` });
  };

  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <div className="h-full flex flex-col px-12 overflow-auto">
      <h1 className="text-xl font-semibold text-primary mb-2">Add new Merchant</h1>
      <button
        type="button"
        onClick={() => navigate({ to: "/admin/customers" })}
        className="flex items-center gap-1.5 text-sm font-medium text-tertiary hover:text-primary transition-colors mb-4 w-fit"
      >
        <ArrowLeft className="size-4" />
        Back to all merchants
      </button>

      {shopsLoading ? (
        <LoadingIndicator size="sm" label="Loading..." />
      ) : (
        <form
          onSubmit={(e) => {
            if (!isLastStep) {
              e.preventDefault();
              return;
            }
            form.handleSubmit(onSubmit)(e);
          }}
          className="flex flex-col flex-1 min-h-0"
        >
          <div
            style={{ gridTemplateColumns: `repeat(${STEPS.length}, minmax(0, 1fr))` }}
            className="grid w-full items-start justify-start gap-4"
          >
            {STEPS.map((step, index) => (
              <button key={step.id} type="button" onClick={() => handleStepClick(index)} className="cursor-pointer">
                <IconTopNumber
                  title={step.label}
                  description=""
                  status={index < currentStep ? "complete" : index === currentStep ? "current" : "incomplete"}
                  step={index + 1}
                  size="sm"
                  connector={index !== STEPS.length - 1}
                />
              </button>
            ))}
          </div>

          <div
            ref={contentRef}
            className={`flex-1 min-h-0 pt-8 overflow-auto ${currentStep >= 3 ? "flex flex-col" : ""}`}
          >
            <div
              className={
                currentStep >= 3
                  ? "flex flex-col flex-1 overflow-hidden max-w-2xl mx-auto w-full"
                  : "flex flex-col gap-8 max-w-2xl mx-auto pb-8"
              }
            >
              {currentStep === 0 && (
                <div className="flex flex-col gap-6">
                  <h2 className="text-md font-semibold text-primary">TikTok Shop</h2>

                  <FormField
                    control={form.control}
                    name="selectedShop"
                    rules={{ required: "Select a TikTok Shop" }}
                    render={({ field, fieldState }) => (
                      <FormItem className="flex flex-col">
                        <Select
                          label="Shop"
                          popoverClassName="dark-mode"
                          placeholder="Select a shop"
                          isRequired
                          isInvalid={!!fieldState.error}
                          hint={fieldState.error?.message}
                          size="md"
                          items={availableShops
                            .map((shop) => ({
                              id: shop.key,
                              label: `${shop.shopName} (${shop.region})`,
                              supportingText: shop.sellerName,
                            }))
                            .sort((a, b) => a.label.localeCompare(b.label))}
                          selectedKey={field.value || null}
                          onSelectionChange={(v) => field.onChange(v)}
                        >
                          {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                              {item.label}
                            </Select.Item>
                          )}
                        </Select>
                      </FormItem>
                    )}
                  />

                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col pr-2">
                        <span className="text-sm font-medium text-secondary">Sub-brand</span>
                        <span className="text-sm text-tertiary">
                          Merchant will only have access to products belonging to this sub-brand (e.g. Twix under
                          Mars-Wrigley). Not required for most shops.
                        </span>
                      </div>
                      <Toggle
                        value={showBrandSelect ? "true" : "false"}
                        onChange={(checked) => {
                          setShowBrandSelect(checked);
                          if (!checked) form.setValue("selectedBrand", "");
                        }}
                        isDisabled={!selectedShop}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="selectedBrand"
                      rules={{ required: showBrandSelect ? "Select a brand" : false }}
                      render={({ field, fieldState }) => (
                        <FormItem className="flex flex-col">
                          <Select
                            placeholder={brandsLoading && showBrandSelect ? "Loading brands..." : "Select a brand"}
                            size="md"
                            isRequired={showBrandSelect}
                            isDisabled={!showBrandSelect}
                            isInvalid={!!fieldState.error}
                            hint={fieldState.error?.message}
                            items={(brandsData?.brands ?? []).map((brand) => ({
                              id: brand.id,
                              label: brand.name,
                            }))}
                            selectedKey={field.value || null}
                            onSelectionChange={(v) => field.onChange(v)}
                            popoverClassName="dark-mode"
                          >
                            {(item) => (
                              <Select.Item key={item.id} id={item.id}>
                                {item.label}
                              </Select.Item>
                            )}
                          </Select>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              )}

              {currentStep === 1 && (
                <div className="flex flex-col gap-6">
                  <h2 className="text-md font-semibold text-primary">Merchant details</h2>

                  <FormField
                    control={form.control}
                    name="brandLongName"
                    rules={{ required: "Merchant full name is required" }}
                    render={({ field, fieldState }) => (
                      <FormItem className="flex flex-col">
                        <Input
                          label="Merchant Full Name"
                          hint={
                            fieldState.error?.message ||
                            'e.g. "Eyes Lips Face (e.l.f) cosmetics" (provides the LLM additional context)'
                          }
                          isInvalid={!!fieldState.error}
                          placeholder="Full merchant name"
                          size="md"
                          isRequired
                          {...field}
                        />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="brandHandle"
                    rules={{ required: "Merchant handle is required" }}
                    render={({ field, fieldState }) => (
                      <FormItem className="flex flex-col">
                        <Input
                          label="Merchant TikTok Handle"
                          hint={fieldState.error?.message || 'e.g. "@elfyeah"'}
                          isInvalid={!!fieldState.error}
                          placeholder="Merchant TikTok username"
                          size="md"
                          isRequired
                          {...field}
                        />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    rules={{ required: "Description is required" }}
                    render={({ field, fieldState }) => (
                      <FormItem className="flex flex-col">
                        <TextArea
                          hint={
                            fieldState.error?.message ||
                            "Provide LLM context about the merchant e.g. \u201ca well-known American cosmetics brand providing a full range of beauty products\u201d"
                          }
                          isInvalid={!!fieldState.error}
                          label="Description"
                          placeholder="Merchant description"
                          isRequired
                          {...field}
                        />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="vertical"
                    rules={{ required: "Vertical is required" }}
                    render={({ field, fieldState }) => (
                      <FormItem className="flex flex-col">
                        <Select
                          label="Vertical"
                          placeholder="Select a vertical"
                          popoverClassName="dark-mode"
                          size="md"
                          isInvalid={!!fieldState.error}
                          hint={fieldState.error?.message}
                          items={VERTICALS}
                          selectedKey={field.value || null}
                          isRequired
                          onSelectionChange={(v) => field.onChange(v)}
                        >
                          {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                              {item.label}
                            </Select.Item>
                          )}
                        </Select>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="id"
                    rules={{ required: "Merchant ID is required" }}
                    render={({ field, fieldState }) => (
                      <FormItem className="flex flex-col">
                        <Input
                          isRequired
                          isInvalid={!!fieldState.error}
                          label="Merchant ID"
                          placeholder="merchant-id"
                          size="md"
                          hint={fieldState.error?.message || "Internal ID; do NOT change unless there are conflicts"}
                          {...field}
                        />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {currentStep === 2 && (
                <div className="flex flex-col gap-6">
                  <h2 className="text-md font-semibold text-primary">Configuration</h2>

                  <FormField
                    control={form.control}
                    name="brandShortName"
                    rules={{ required: "Short name is required" }}
                    render={({ field, fieldState }) => (
                      <FormItem className="flex flex-col">
                        <Input
                          label="Short Name"
                          placeholder="Short merchant name"
                          size="md"
                          isRequired
                          isInvalid={!!fieldState.error}
                          hint={
                            fieldState.error?.message || "Commonly used merchant name; how it will be named in the app"
                          }
                          {...field}
                        />
                      </FormItem>
                    )}
                  />

                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-secondary">Logo *</span>
                    <LogoPicker
                      currentLogoUrl={logoPreviewUrl}
                      onUpload={async (file) => {
                        setLogoFile(file);
                        setLogoPreviewUrl(URL.createObjectURL(file));
                        setLogoError(null);
                      }}
                      onDelete={async () => {
                        setLogoFile(null);
                        if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
                        setLogoPreviewUrl(null);
                      }}
                    />
                    {logoError && <span className="text-sm text-error-primary">{logoError}</span>}
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-secondary">Features</span>
                    <CheckboxGroup
                      value={selectedFeatures}
                      onChange={(f) => setSelectedFeatures(f as Feature[])}
                      size="sm"
                      items={AVAILABLE_FEATURES.map((f) => ({
                        value: f,
                        title: FEATURE_LABELS[f],
                        description: FEATURE_DESCRIPTIONS[f],
                      }))}
                      render={(_item) => {
                        const item = _item as { value: string; title: string; description?: string };
                        return (
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-semibold text-secondary">{item.title}</span>
                              {item.value === "noAffiliateAuthorisation" && missingAffiliateScopes.length > 0 && (
                                <Tooltip
                                  title="Missing TikTok scopes"
                                  description={`This shop is missing: ${missingAffiliateScopes.join(", ")}. Affiliate messaging and collaboration features will not work.`}
                                >
                                  <TooltipTrigger>
                                    <AlertTriangle className="size-4 text-warning" />
                                  </TooltipTrigger>
                                </Tooltip>
                              )}
                            </div>
                            {item.description && <span className="text-sm text-tertiary">{item.description}</span>}
                          </div>
                        );
                      }}
                    />
                  </div>
                </div>
              )}
              {currentStep === 3 && (
                <div className="flex flex-col flex-1 overflow-hidden gap-4" style={{ minHeight: "400px" }}>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Select
                      isRequired
                      label="Owner workspace"
                      placeholder="Select the workspace that owns this merchant"
                      size="md"
                      hint={ownerError ?? "The workspace that owns this merchant"}
                      isInvalid={!!ownerError}
                      items={workspaceItems}
                      selectedKey={ownerWorkspaceId}
                      onSelectionChange={(key) => {
                        setOwnerWorkspaceId(key ? String(key) : null);
                        setOwnerError(null);
                      }}
                      popoverClassName="dark-mode"
                    >
                      {(workspace) => (
                        <Select.Item key={workspace.id} id={workspace.id}>
                          {workspace.label}
                        </Select.Item>
                      )}
                    </Select>
                  </div>
                  <WorkspacesPicker
                    merchantName={form.watch("brandShortName") || form.watch("brandLongName") || "this merchant"}
                    value={selectedWorkspaceIds}
                    onChange={setSelectedWorkspaceIds}
                  />
                </div>
              )}

              {currentStep === 4 && (
                <div className="flex flex-col flex-1 overflow-hidden" style={{ minHeight: "400px" }}>
                  <WorkspaceUsersPicker value={userRoles} onChange={setUserRoles} roleOptions={merchantScopeRoles} />
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between w-full border-t border-tertiary pt-4 pb-2">
            {currentStep > 0 ? (
              <Button color="secondary" size="lg" type="button" onClick={() => goToStep(currentStep - 1)}>
                Back
              </Button>
            ) : (
              <Button color="secondary" size="lg" onClick={() => navigate({ to: "/admin/customers" })}>
                Cancel
              </Button>
            )}

            {!isLastStep && (
              <Button color="primary" size="lg" type="button" onClick={handleNext}>
                Next
              </Button>
            )}
            {isLastStep && (
              <Button
                color="primary"
                size="lg"
                type="submit"
                // Gate only on the reactive business requirements; the <form> onSubmit runs
                // form.handleSubmit which validates the RHF fields before onSubmit fires. Relying on
                // form.formState.isValid here left the button stale on the last step (only per-step
                // fields get triggered), so it stayed disabled until a step re-navigation revalidated.
                isDisabled={form.formState.isSubmitting || !selectedShop || !logoFile}
              >
                Create Merchant
              </Button>
            )}
          </div>
        </form>
      )}
    </div>
  );
};
